import type {
  MatchOperation,
  MatchSource,
  OpFilterMode,
} from '../types/matchEvents.types';
import { parseTsToMillis, type MatchEventRow } from './matchEventsFeed';

/**
 * Live-hub operation helpers — pure derivation for the `match_operation`
 * side of the feed (the sniper/manual actions) and the active-match `source`
 * badge. Kept free of React / DOM so the classifier, filter predicate, badge
 * mapping, summary formatter and the incident+operation merge are trivially
 * unit-testable. Mirrors the opener's auto (⚡) / manual (✋) split.
 */

/** Automatic vs manual — the two op lanes (mirrors the opener notifications). */
export type OpKind = 'automatic' | 'manual';

/** Op `type`s emitted by the sniper (automatic lane). */
export const AUTOMATIC_OP_TYPES = new Set<string>([
  'sniper_fired',
  'sniper_auto_no_fired',
  'sniper_var_reversal',
  'sniper_var_rebuy',
  'sniper_fire_blocked',
  'sniper_ladder_sell_placed',
  'sniper_ladder_sell_amended',
  'sniper_ladder_sell_filled',
  'sniper_buy_filled',
  'sniper_goal_retracted',
]);

/** Op `type`s emitted by an operator action (manual lane). */
export const MANUAL_OP_TYPES = new Set<string>(['manual_order']);

/**
 * Resolve an operation's lane. The wire `kind` is authoritative; when it is
 * missing / unrecognised we fall back to the `type` registry (manual types →
 * manual, everything else → automatic), so a new sniper op type still lands in
 * the automatic lane rather than vanishing.
 */
export function classifyOperation(op: {
  kind?: string | null;
  type: string;
}): OpKind {
  if (op.kind === 'automatic') return 'automatic';
  if (op.kind === 'manual') return 'manual';
  if (MANUAL_OP_TYPES.has(op.type)) return 'manual';
  return 'automatic';
}

/** The Auto | Manual | All op-filter predicate. */
export function matchesOpFilter(
  op: { kind?: string | null; type: string },
  mode: OpFilterMode,
): boolean {
  if (mode === 'all') return true;
  const kind = classifyOperation(op);
  return mode === 'auto' ? kind === 'automatic' : kind === 'manual';
}

/** Visual config for an op row (⚡ automatic = blue, ✋ manual = purple). */
export interface OperationDisplay {
  emoji: string;
  color: string;
  label: string;
  kind: OpKind;
}

export function operationDisplay(op: {
  kind?: string | null;
  type: string;
}): OperationDisplay {
  const kind = classifyOperation(op);
  return kind === 'automatic'
    ? { emoji: '⚡', color: '#42a5f5', label: 'automatic', kind }
    : { emoji: '✋', color: '#ab47bc', label: 'manual', kind };
}

/** The provenance badge for an active match (⚡ group / ✋ manual / both). */
export interface SourceBadge {
  emoji: string;
  label: string;
  color: string;
}

export function sourceBadge(source: MatchSource): SourceBadge {
  switch (source) {
    case 'group':
      return { emoji: '⚡', label: 'group', color: '#42a5f5' };
    case 'manual':
      return { emoji: '✋', label: 'manual', color: '#ab47bc' };
    case 'both':
      return { emoji: '⚡✋', label: 'both', color: '#7e57c2' };
    default:
      return { emoji: '•', label: String(source), color: '#9e9e9e' };
  }
}

/** Round to at most 4 decimals and drop trailing zeros ("0.8300" → "0.83"). */
function trimNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return Number(n.toFixed(4)).toString();
}

/** filled/requested size string, or the single side that's present. */
export function formatOperationSize(op: MatchOperation): string {
  // A PLACEMENT-only frame's `filled` is not a fill count — the ladder's
  // `sniper_ladder_sell_placed` reports `held`, the size it just rested. So
  // "100/100" would read as a completed fill under a "placed" chip. Render the
  // resting size alone instead (`sizeRequested` is the original sweep size, not
  // what is on the book, so `filled` is the better reading when both exist).
  if (isPlacementOnlyType(op.type)) {
    const resting = op.filled ?? op.sizeRequested ?? null;
    return resting == null ? '' : trimNumber(resting);
  }
  const { filled, sizeRequested } = op;
  if (filled != null && sizeRequested != null) {
    // Drop the denominator once the order is complete. `filled` can EXCEED
    // `sizeRequested` — a sale-and-rebuy cycle accumulates against the original
    // sweep size — and "120/100" reads as a broken counter rather than a filled
    // order.
    if (filled >= sizeRequested) return trimNumber(filled);
    return `${trimNumber(filled)}/${trimNumber(sizeRequested)}`;
  }
  if (sizeRequested != null) return trimNumber(sizeRequested);
  if (filled != null) return trimNumber(filled);
  return '';
}

/**
 * One-line human summary of an operation: `venue · market · SIDE · size @ price
 * — reason`. Every segment is optional; missing fields are omitted (no empty
 * separators), and `reason` (when present) is appended after an em dash.
 */
export function formatOperationSummary(op: MatchOperation): string {
  const parts: string[] = [];
  if (op.venue) parts.push(op.venue);
  const market = op.marketLabel ?? op.marketId;
  if (market) parts.push(String(market));
  if (op.side) parts.push(op.side.toUpperCase());
  const size = formatOperationSize(op);
  if (size) parts.push(size);
  const price = op.avgPrice ?? op.price;
  if (price != null) parts.push(`@ ${trimNumber(price)}`);
  let summary = parts.join(' · ');
  if (op.reason) summary = summary ? `${summary} — ${op.reason}` : op.reason;
  return summary;
}

/**
 * The order-lifecycle vocabulary the Sniper card renders, mirroring how the
 * opener's notifications page reads a fill: which LEG moved (the BUY entry vs
 * the SELL exit) and how far the order got (placed → partial → filled).
 */
export type OpLifecycleLeg = 'buy' | 'sell';
export type OpLifecyclePhase = 'placed' | 'partial' | 'filled';

export interface OpLifecycle {
  leg: OpLifecycleLeg;
  phase: OpLifecyclePhase;
}

/** Which leg each order-lifecycle op type belongs to. */
const LIFECYCLE_LEG: Record<string, OpLifecycleLeg> = {
  // The FAK sweep IS the entry order; it re-emits as its `filled` climbs.
  sniper_fired: 'buy',
  // The period-close auto-NO tail — a GTC BUY on the No/Under side.
  sniper_auto_no_fired: 'buy',
  // The VAR all-clear buy-back.
  sniper_var_rebuy: 'buy',
  // Every booked BUY fill delta (cumulative `filled` + `sizeRequested`).
  sniper_buy_filled: 'buy',
  // The confidence ladder's resting exit.
  sniper_ladder_sell_placed: 'sell',
  sniper_ladder_sell_amended: 'sell',
  sniper_ladder_sell_filled: 'sell',
  // The VAR-defense exit (break-even / dump-to-floor) is a SELL.
  sniper_var_reversal: 'sell',
};

/**
 * Types whose `filled` is NOT an executed quantity. The engine reuses that field
 * for whatever size the frame is about, so a consumer reading it as "how much
 * executed" invents an execution that never happened:
 *   - `sniper_ladder_sell_placed` / `_amended` → `held`, the size just rested;
 *   - `sniper_var_reversal` → `held` (`alert_reversal`), what we still hold and
 *     have just placed a GTC exit for;
 *   - `sniper_var_rebuy` → the buy-back quantity (`alert_var_defense`).
 * All four also carry `avgPrice: None` and a hardcoded `sizeRequested =
 * cfg.size`, so rendering them as fills produced a fabricated "60 of 100 sold"
 * for what was really "a sell for 60 was just placed". Their REAL executions
 * arrive separately as ladder-sell / buy fills.
 */
const PLACEMENT_ONLY_TYPES = new Set<string>([
  'sniper_ladder_sell_placed',
  'sniper_ladder_sell_amended',
  'sniper_var_reversal',
  'sniper_var_rebuy',
]);

/** True when this op's `filled` is a resting size, not an executed quantity. */
export function isPlacementOnlyType(type: string): boolean {
  return PLACEMENT_ONLY_TYPES.has(type);
}

/** The leg an op type belongs to, or null when it is not order lifecycle. */
export function operationLeg(type: string): OpLifecycleLeg | null {
  return LIFECYCLE_LEG[type] ?? null;
}

/**
 * Which ORDER SERIES within a (mark, leg) an op belongs to.
 *
 * A mark's buy leg carries TWO independent orders. The period-close auto-NO
 * tail buys the No/Under side of the same market, and the engine builds its
 * notification from the SAME `SniperMarketConfig` as the main sweep — so it
 * reports the identical `marketRuleId` and `LIFECYCLE_LEG` maps it to `'buy'`
 * as well. Only the `" (NO)"` suffix the emitter appends to `marketLabel`
 * distinguishes them on the wire. Keying a cell by (mark, leg) alone therefore
 * put the sweep and its tail in ONE cell, and since the tail fires at the
 * period close — often 40 minutes later — its newer `ts` replaced the sweep's
 * execution and the position that actually matters vanished from the card.
 *
 * `sniper_var_rebuy` stays on the MAIN strand: it buys the main position back
 * after a VAR all-clear, it is not a separate order series.
 */
export type OpLifecycleStrand = 'main' | 'auto_no';

export function operationStrand(type: string): OpLifecycleStrand {
  return type === 'sniper_auto_no_fired' ? 'auto_no' : 'main';
}

/**
 * How far an order-lifecycle op got. Partial-vs-complete is DERIVED from the
 * cumulative `filled` against `sizeRequested` — the backend does not split them
 * into separate types — so:
 *   - nothing filled yet (missing / ≤ 0) → `placed`;
 *   - filled short of the requested size → `partial`;
 *   - filled up to (or past, or with no known total) the requested size →
 *     `filled`.
 * Returns null for an op that is not part of the order lifecycle at all.
 */
export function operationLifecycle(
  op: Pick<MatchOperation, 'type' | 'filled' | 'sizeRequested'>,
): OpLifecycle | null {
  const leg = LIFECYCLE_LEG[op.type];
  if (!leg) return null;
  if (PLACEMENT_ONLY_TYPES.has(op.type)) return { leg, phase: 'placed' };
  const filled = op.filled ?? null;
  if (filled == null || filled <= 0) return { leg, phase: 'placed' };
  const total = op.sizeRequested ?? null;
  if (total != null && filled < total) return { leg, phase: 'partial' };
  return { leg, phase: 'filled' };
}

/** Operator-facing wording for a lifecycle, e.g. "BUY partial fill". */
export function formatOperationLifecycle(lifecycle: OpLifecycle): string {
  const phase =
    lifecycle.phase === 'partial' ? 'partial fill' : lifecycle.phase;
  return `${lifecycle.leg.toUpperCase()} ${phase}`;
}

/**
 * A stable de-dupe / React key for an operation (no streamId on the wire).
 * `venue` and `reason` are part of the key so two DISTINCT ops for the same
 * incident/market/side at the same `ts` — e.g. two `sniper_fire_blocked`
 * differing only by venue (Kalshi vs Poly on a null/shared marketId) or by
 * reason — do not collide and get silently dropped by the seen-ops guard.
 */
export function operationKey(op: MatchOperation): string {
  return [
    op.fixtureId,
    op.type,
    op.ts,
    op.incidentKey ?? '',
    op.marketId ?? '',
    op.side ?? '',
    op.venue ?? '',
    op.reason ?? '',
  ].join('|');
}

/**
 * The relay's namespace for every op that COLLAPSES onto one row. Treated as an
 * OPAQUE prefix — deliberately never parsed apart.
 *
 * `resolveOpStreamId` (notifications-websocket) is the single authority on op
 * identity, and it guarantees exactly two things:
 *   - a collapsing op gets a key beginning with `sniper:` — today
 *     `sniper:<orderId>` for the fires, `sniper:buyfill:<orderId>` and
 *     `sniper:sellfill:<orderId>` for the fill series;
 *   - every other op keeps the raw Redis entry id, shaped `<millis>-<seq>`,
 *     which can never begin with `sniper:`.
 * That is the whole contract the frontend needs. Slicing an order id back out
 * of the key would re-hardcode today's namespace list, and the backend is
 * actively re-keying the buy-fill row to be MARK-scoped (a mark's gross is
 * mark-level, and the venue `orderId` changes when the VAR all-clear rebuy
 * re-places the buy) — a shape that does not exist yet. Matching on the prefix
 * and using the key verbatim survives that change.
 */
const SNIPER_ID_PREFIX = 'sniper:';

/**
 * The STABLE identity of an op, or null when it has none.
 *
 * `streamId` is the ONLY identity present on both paths — the live frame is
 * stamped before it is emitted, and the Mongo backfill persists it — whereas
 * `orderId` is live-only (it is not on the schema and not in `toOperationFrame`).
 * So the key must come from `streamId` alone: deriving it from `orderId` made
 * the same order collapse differently live than after a refresh, and produced
 * two rows that never reconciled.
 *
 * Which ops share an identity is the BACKEND's decision, not ours. It keys
 * `sniper_fired` and `sniper_buy_filled` into DIFFERENT namespaces on purpose,
 * so the fill's `$set` cannot flip the placement row's `type` in place; the
 * frontend must not merge them either. Ops with a raw entry id — blocked fires,
 * retractions, the ladder place/amend events, VAR reversals, every manual op —
 * have no stable identity and stay append-only via {@link operationKey}.
 *
 * The relay's key is the ONLY source consulted. An earlier `op.id` override was
 * a second, ungated identity path in a function whose whole thesis is that the
 * relay owns identity — and nothing has ever produced that field.
 */
export function operationStableId(op: { streamId?: string | null }): string | null {
  if (op.streamId?.startsWith(SNIPER_ID_PREFIX)) return op.streamId;
  return null;
}

/** Shallow value-equality over the union of both objects' own keys. */
function sameOpContent(a: MatchOperation, b: MatchOperation): boolean {
  const ra = a as unknown as Record<string, unknown>;
  const rb = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(ra), ...Object.keys(rb)]);
  for (const k of keys) {
    if (ra[k] !== rb[k]) return false;
  }
  return true;
}

/**
 * The monotonic fill floor an op establishes, or null when it establishes none.
 * A PLACEMENT-only frame carries `filled = held` (a resting size, not an
 * executed quantity), so it must neither be compared against a real fill nor
 * seed a floor — otherwise the ladder's `sell_placed(filled=100)` would swallow
 * every subsequent `sell_filled(filled=40)` as "stale".
 */
function fillFloor(op: MatchOperation): number | null {
  if (isPlacementOnlyType(op.type)) return null;
  return op.filled ?? 0;
}

/**
 * Merge a fill-update onto the existing op for the same stable id. `filled`
 * climbs monotonically as the venue order fills, so a stale/out-of-order frame
 * whose `filled` is strictly LOWER is dropped (the trivial guard); otherwise the
 * incoming frame wins (latest-wins for `filled`/`avgPrice`/`ts`/…). Returns the
 * SAME reference when nothing changed so the caller can skip a state update.
 */
function mergeSniperOp(
  existing: MatchOperation,
  incoming: MatchOperation,
): MatchOperation {
  // A strictly OLDER frame always loses, whatever its fill semantics. This is
  // the guard that still holds once a PLACEMENT is the stored row: a placement
  // reports no fill, so it establishes no floor, and without this a stale
  // out-of-order `sell_filled(40)` arriving after a re-amend could win.
  if (parseTsToMillis(incoming.ts) < parseTsToMillis(existing.ts)) {
    return existing;
  }
  const existingFloor = fillFloor(existing);
  const incomingFloor = fillFloor(incoming);
  // Only compare two genuine fill reports. When either side is a placement its
  // `filled` is a resting size, so there is nothing to compare and the newer
  // frame wins (a re-amend after a partial fill legitimately replaces the row).
  if (
    existingFloor != null &&
    incomingFloor != null &&
    incomingFloor < existingFloor
  ) {
    return existing;
  }
  const merged = { ...existing, ...incoming };
  // The gateway backfills durable ops on EVERY `subscribe_match`, so the exact
  // same frame can arrive twice. Returning `existing` unchanged lets the caller
  // skip the state update (and the re-render) entirely.
  return sameOpContent(merged, existing) ? existing : merged;
}

/**
 * Fold one incoming `match_operation` into the operations list. An op with a
 * stable id ({@link operationStableId}) that already exists is UPDATED IN PLACE
 * (same index → feed order preserved); everything else is appended, capped at
 * `maxRows` (oldest evicted). Pure — the hook owns the seen-key de-dupe of exact
 * re-deliveries; this handles the fill-climb of a re-emitted fire.
 */
export function reconcileOperations(
  prev: MatchOperation[],
  op: MatchOperation,
  maxRows: number,
): MatchOperation[] {
  const id = operationStableId(op);
  if (id) {
    const idx = prev.findIndex((o) => operationStableId(o) === id);
    if (idx !== -1) {
      const merged = mergeSniperOp(prev[idx], op);
      if (merged === prev[idx]) return prev;
      const next = prev.slice();
      next[idx] = merged;
      return next;
    }
  }
  const next = [...prev, op];
  return next.length > maxRows ? next.slice(next.length - maxRows) : next;
}

/**
 * One row of the card's order strip: the newest lifecycle frame for one
 * (mark, leg, strand) — one order series. A card holds MANY marks (it subgroups
 * them into Gol / Corner) and a mark's buy leg holds TWO orders (the sweep and
 * the period-close auto-NO tail), so all three parts are needed to keep
 * genuinely different orders off each other's row.
 */
export interface OrderLegRow {
  /** The sniper MARK (`marketRuleId`) — the market identity, not the ticker. */
  marketRuleId: string;
  marketLabel: string | null;
  leg: OpLifecycleLeg;
  strand: OpLifecycleStrand;
  op: MatchOperation;
}

/** Keyed `<marketRuleId>:<leg>:<strand>`; iteration order is insertion order. */
export type OrderLegs = ReadonlyMap<string, OrderLegRow>;

/** Legs in render order, so a mark's rows never swap under the operator. */
export const ORDER_LEG_SEQUENCE: OpLifecycleLeg[] = ['buy', 'sell'];

/** Strands in render order: the main sweep first, its auto-NO tail after. */
export const ORDER_STRAND_SEQUENCE: OpLifecycleStrand[] = ['main', 'auto_no'];

/**
 * How many MARKS the strip shows. The cap is per mark, never per row: evicting
 * individual cells would drop a mark's BUY while keeping its SELL, leaving a
 * bare `SELL filled` with no entry context — worse than not showing the mark.
 * A Sniper match runs ~10-20 marks; the strip is a status line, not the feed,
 * so the least-recently-active mark drops out whole.
 */
export const MAX_ORDER_MARKS = 4;

/**
 * A pre-`marketRuleId` op still gets a stable bucket. Falls back to the venue
 * ticker rather than a shared constant so two such ops on different markets
 * still separate — the ticker is a weaker identity, not no identity.
 */
function marketIdentity(op: MatchOperation): string {
  return op.marketRuleId ?? op.marketId ?? '(unknown market)';
}

export function orderLegKey(
  marketRuleId: string,
  leg: OpLifecycleLeg,
  strand: OpLifecycleStrand,
): string {
  return `${marketRuleId}:${leg}:${strand}`;
}

/**
 * Fold one op into the per-(mark, leg, strand) view, each cell keeping its
 * NEWEST frame.
 *
 * By MARK because a card is not one order: goals-over-1.5 and corners-over-8.5
 * fire separately, and a leg-only key let the corner fill two minutes later
 * REPLACE the goal fill. By STRAND because the auto-NO tail reports the SAME
 * `marketRuleId` and the same `'buy'` leg as the sweep it follows, so without it
 * the tail firing at the period close replaced the sweep's execution.
 *
 * `marketRuleId`, not `marketId`: the latter is the VENUE ticker, and two
 * sniper marks can point at the same one, so it is not an identity. The backend
 * keys the buy-fill row the same way for the same reason.
 *
 * Newest-per-cell (rather than a flat FIFO) because a refresh replays the
 * durable ops, and what the card is trying to say is where each order ENDED UP,
 * not how it got there.
 *
 * Pure, and returns `prev` unchanged for a non-lifecycle op, an out-of-order
 * frame, or an exact re-delivery — so a duplicated backfill is a no-op.
 */
export function reconcileOrderLegs(
  prev: OrderLegs,
  op: MatchOperation,
  maxMarks: number = MAX_ORDER_MARKS,
): OrderLegs {
  const lifecycle = operationLifecycle(op);
  if (!lifecycle) return prev;
  const marketRuleId = marketIdentity(op);
  const strand = operationStrand(op.type);
  const key = orderLegKey(marketRuleId, lifecycle.leg, strand);
  const current = prev.get(key);
  if (current) {
    if (parseTsToMillis(op.ts) < parseTsToMillis(current.op.ts)) return prev;
    if (sameOpContent(current.op, op)) return prev;
  }
  const next = new Map(prev);
  // Delete first so an UPDATED cell moves to the end of the insertion order —
  // the recency the mark-level eviction below reads off.
  next.delete(key);
  next.set(key, {
    marketRuleId,
    marketLabel: op.marketLabel ?? null,
    leg: lifecycle.leg,
    strand,
    op,
  });
  evictOldestMarks(next, maxMarks);
  return next;
}

/**
 * Drop whole marks — every cell of one — until at most `maxMarks` remain, oldest
 * first. A mark's recency is its NEWEST cell, so a mark whose sell just filled
 * survives even if its buy is an hour old.
 */
function evictOldestMarks(cells: Map<string, OrderLegRow>, maxMarks: number): void {
  const newest = markRecency(cells.values());
  if (newest.size <= maxMarks) return;
  const doomed = [...newest.entries()]
    .sort((a, b) => a[1] - b[1])
    .slice(0, newest.size - maxMarks)
    .map(([markId]) => markId);
  if (doomed.length === 0) return;
  const drop = new Set(doomed);
  for (const [key, row] of [...cells]) {
    if (drop.has(row.marketRuleId)) cells.delete(key);
  }
}

/** Each mark's recency = the `ts` of its NEWEST cell. */
function markRecency(rows: Iterable<OrderLegRow>): Map<string, number> {
  const newest = new Map<string, number>();
  for (const row of rows) {
    const ts = parseTsToMillis(row.op.ts);
    const seen = newest.get(row.marketRuleId);
    if (seen === undefined || ts > seen) newest.set(row.marketRuleId, ts);
  }
  return newest;
}

/**
 * The strip's render order: marks by their NEWEST activity (most recent FIRST,
 * so a mark that just moved is at the top), and within a mark always BUY before
 * SELL, main sweep before its auto-NO tail.
 *
 * Ordering on the newest cell, not the oldest: a mark whose SELL just filled but
 * whose BUY is 40 minutes old is the one the operator needs to see, and grouping
 * by first-seen buried it under marks that had been quiet for ten.
 */
export function orderLegRows(legs: OrderLegs): OrderLegRow[] {
  const byMark = new Map<string, OrderLegRow[]>();
  for (const row of legs.values()) {
    const rows = byMark.get(row.marketRuleId);
    if (rows) rows.push(row);
    else byMark.set(row.marketRuleId, [row]);
  }
  const recency = markRecency(legs.values());
  const marks = [...byMark.keys()].sort(
    (a, b) => (recency.get(b) ?? 0) - (recency.get(a) ?? 0),
  );
  return marks.flatMap((marketRuleId) =>
    ORDER_LEG_SEQUENCE.flatMap((leg) =>
      ORDER_STRAND_SEQUENCE.flatMap(
        (strand) =>
          byMark
            .get(marketRuleId)
            ?.filter((r) => r.leg === leg && r.strand === strand) ?? [],
      ),
    ),
  );
}

/**
 * Bound a seen-op-key Set in place: evict the oldest (insertion-ordered) keys
 * until `size <= max`. Deleting visited entries mid-iteration is safe for a Set.
 * Used to cap the hook's unbounded de-dupe memory over a long session.
 */
export function boundSeenOps(seen: Set<string>, max: number): void {
  if (max < 0) max = 0;
  if (seen.size <= max) return;
  let toEvict = seen.size - max;
  for (const k of seen) {
    if (toEvict-- <= 0) break;
    seen.delete(k);
  }
}

/** A synthetic "end of period" marker (shape mirrors matchEventsFeed). */
export interface FeedMarker {
  key: string;
  label: string;
  receivedAt: string;
}

/** One entry in the merged per-match feed (incident | operation | marker). */
export type MatchFeedEntry =
  | { kind: 'incident'; ts: string; key: string; row: MatchEventRow }
  | { kind: 'operation'; ts: string; key: string; op: MatchOperation }
  | { kind: 'marker'; ts: string; key: string; label: string };

/**
 * Merge incident rows, operations and period markers into ONE feed, ordered
 * newest-first by timestamp (`receivedAt` for incidents/markers, `ts` for
 * operations). Purely a merge + sort — filtering happens upstream.
 */
export function mergeMatchFeed(
  rows: MatchEventRow[],
  operations: MatchOperation[],
  markers: FeedMarker[] = [],
): MatchFeedEntry[] {
  const entries: MatchFeedEntry[] = [
    ...rows.map((row) => ({
      kind: 'incident' as const,
      ts: row.receivedAt,
      key: row.frame.streamId || row.collapseKey,
      row,
    })),
    ...operations.map((op) => ({
      kind: 'operation' as const,
      ts: op.ts,
      // A sniper fire's stable id keeps its row identity stable across fill
      // updates (climb 0 → 78) so the row refreshes in place rather than
      // remounting; ops without one fall back to the composite key.
      key: operationStableId(op) ?? operationKey(op),
      op,
    })),
    ...markers.map((m) => ({
      kind: 'marker' as const,
      ts: m.receivedAt,
      key: m.key,
      label: m.label,
    })),
  ];
  entries.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  return entries;
}
